import { Injectable, signal } from '@angular/core';

export interface DownloadFeedbackState {
  visible: boolean;
  fileName: string;
}

const DISPLAY_DURATION_MS = 2600;

@Injectable({ providedIn: 'root' })
export class DownloadFeedbackService {
  private readonly state = signal<DownloadFeedbackState>({ visible: false, fileName: '' });
  readonly feedback = this.state.asReadonly();

  private dismissTimeoutId?: ReturnType<typeof setTimeout>;
  private restartFrameId?: ReturnType<typeof requestAnimationFrame>;

  notifyDownloaded(fileName: string): void {
    this.clearTimers();

    // Reset first so re-triggering while already visible restarts the animation
    // instead of the overlay silently staying in its current state.
    this.state.set({ visible: false, fileName: '' });

    this.restartFrameId = requestAnimationFrame(() => {
      this.state.set({ visible: true, fileName });
      this.dismissTimeoutId = setTimeout(() => this.dismiss(), DISPLAY_DURATION_MS);
    });
  }

  dismiss(): void {
    this.clearTimers();
    this.state.update((current) => ({ ...current, visible: false }));
  }

  private clearTimers(): void {
    if (this.dismissTimeoutId) {
      clearTimeout(this.dismissTimeoutId);
      this.dismissTimeoutId = undefined;
    }

    if (this.restartFrameId) {
      cancelAnimationFrame(this.restartFrameId);
      this.restartFrameId = undefined;
    }
  }
}
